import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Coins, FolderOpen, LifeBuoy, Scissors, Sparkles, Zap } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useToolCatalog } from '@/lib/tool-catalog';
import { EDITOR_PAGE_KEYS, editorLang, editorPath } from '@/components/editorPages/routes';

// THE COMMAND MENU (Studio Blueprint 03): Ctrl K / ⌘K on any page, or the
// Search button in the header, finds every studio, quick tool, editor page
// and place in the frame by name. ↑↓ to move, Enter to go, Esc to close.

export const COMMAND_MENU_EVENT = 'site:command-menu';

export function openCommandMenu() {
  window.dispatchEvent(new Event(COMMAND_MENU_EVENT));
}

export function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const catalog = useToolCatalog();
  const navigate = useNavigate();
  const lang = editorLang(i18n.language);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    const onEvent = () => onOpenChange(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(COMMAND_MENU_EVENT, onEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(COMMAND_MENU_EVENT, onEvent);
    };
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('shell.command.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('shell.command.empty')}</CommandEmpty>
        {/* Every tool by what it is for, the pay-per-file ones with the rest
            (owner 2026-10-16). Nothing is locked from here. */}
        {catalog.map((cat) => (
          <CommandGroup key={cat.key} heading={t(`toolCats.${cat.key}`)}>
            {cat.tools.map((tool) => (
              <CommandItem key={tool.id} value={`${tool.name} ${tool.line}${tool.quick ? ` ${t(`qt.tools.${tool.quick}.short`)}` : ''}`} onSelect={() => go(tool.path)}>
                {tool.quick ? <Zap className="mr-2 text-primary" aria-hidden /> : <Sparkles className="mr-2 text-primary" aria-hidden />}
                <span className="flex-1">{tool.name}</span>
                <span className="text-xs text-muted-foreground">{tool.price}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading={t('shell.command.editor')}>
          {EDITOR_PAGE_KEYS.map((key) => (
            <CommandItem key={key} value={t(`editorPages.pages.${key}.h1`)} onSelect={() => go(editorPath(key, lang))}>
              <Scissors className="mr-2 text-accent" aria-hidden />
              <span className="flex-1">{t(`editorPages.pages.${key}.h1`)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading={t('shell.command.places')}>
          <CommandItem value={t('shell.tabs.watch')} onSelect={() => go('/watch')}>
            <Clapperboard className="mr-2" aria-hidden />
            {t('shell.tabs.watch')}
          </CommandItem>
          <CommandItem value={t('shell.tabs.projects')} onSelect={() => go('/projects')}>
            <FolderOpen className="mr-2" aria-hidden />
            {t('shell.tabs.projects')}
          </CommandItem>
          <CommandItem value={`${t('shell.tabs.credits')} ${t('navigation.pricing')}`} onSelect={() => go('/pricing')}>
            <Coins className="mr-2" aria-hidden />
            {t('shell.tabs.credits')}
          </CommandItem>
          <CommandItem value={t('footer.contact')} onSelect={() => go('/contact')}>
            <LifeBuoy className="mr-2" aria-hidden />
            {t('footer.contact')}
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <p className="border-t border-border px-3 py-2 font-mono text-[11px] text-muted-foreground">{t('shell.command.hint')}</p>
    </CommandDialog>
  );
}
